import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import purchaseService from '../services/purchaseService';
import futbolistaService from '../services/futbolistaService';
import sedeService from '../services/sedeService';
import { warn, error as logError, info } from '../utils/logger';
import { formatFechaJornada } from '../utils/fechaUtils';

import ProfileCompletionBanner from '../components/ProfileCompletionBanner';
import useWindowSize from '../hooks/useWindowSize';
import '../styles/profile.css';

/**
 * Componente de perfil de usuario directo (sin PrivateRoute)
 * Maneja la autenticación internamente
 */
const DirectProfile = () => {
  const { user, isAuthenticated } = useAuth();
  const { isMobile, isSmallMobile } = useWindowSize();

  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [sessionsSummary, setSessionsSummary] = useState(null);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [tutorStats, setTutorStats] = useState({
    futbolistasRegistrados: 0,
    futbolistasTemporadaActual: 0,
    inscripcionesActivas: 0,
    torneosActivos: 0,
    temporadaActual: 'N/A'
  });

  const [sedeInfo, setSedeInfo] = useState({
    tiene_sede_cercana: false,
    sede_cercana: null,
    cargando: false
  });
  const navigate = useNavigate();

  // Usar refs para evitar re-renders innecesarios
  const hasLoadedProfile = useRef(false);
  const hasLoadedPurchases = useRef(false);
  const currentUserId = useRef(null);

  // Componente de avatar por defecto simple
  const DefaultAvatar = () => (
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        backgroundColor: '#e9ecef',
        border: '2px solid #adb5bd',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6c757d',
        fontSize: '48px',
        fontWeight: 'bold',
        fontFamily: 'Arial, sans-serif'
      }}
    >
      👤
    </div>
  );

  // Verificar autenticación y cargar datos del perfil
  useEffect(() => {
    const loadProfile = async () => {
      if (!isAuthenticated()) {
        navigate('/login');
        return;
      }

      // Si no hay usuario, no hacer nada
      if (!user) return;

      // Si el usuario cambió, resetear el cache
      if (currentUserId.current !== user._id) {
        hasLoadedProfile.current = false;
        currentUserId.current = user._id;
      }

      // Verificar si ya hemos cargado el perfil para este usuario
      if (hasLoadedProfile.current) {
        return;
      }

      try {
        setLoading(true);

        // Intentar obtener el perfil completo desde el servidor
        const response = await fetch('/api/users/profile', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('prokids_token')}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          info('Perfil cargado desde el servidor', { userId: user._id, phone: data.phone });
          setProfileData(data);
          hasLoadedProfile.current = true;
        } else {
          warn('No se pudo obtener el perfil del servidor, usando datos del contexto');
          setProfileData(user);
          hasLoadedProfile.current = true;
        }
      } catch (error) {
        logError('Error al cargar el perfil', { error: error.message, stack: error.stack });
        setProfileData(user);
        hasLoadedProfile.current = true;
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [isAuthenticated, navigate, user]); // Dependencias controladas con refs

  // Cargar compras del usuario
  useEffect(() => {
    const loadUserPurchases = async () => {
      if (!isAuthenticated() || !user) return;

      // Verificar si ya hemos cargado las compras para este usuario
      if (hasLoadedPurchases.current && currentUserId.current === user._id) {
        return;
      }

      try {
        setPurchasesLoading(true);

        // Cargar compras activas y resumen de sesiones
        const [activePurchasesResponse, sessionsSummaryResponse] = await Promise.all([
          purchaseService.getActivePurchases(),
          purchaseService.getSessionsSummary()
        ]);

        setPurchases(activePurchasesResponse.data || []);
        setSessionsSummary(sessionsSummaryResponse.data || null);
        hasLoadedPurchases.current = true;
      } catch (error) {
        logError('Error al cargar compras del usuario', { error: error.message, stack: error.stack });
        // No mostrar error al usuario, solo registrar en logs
      } finally {
        setPurchasesLoading(false);
      }
    };

    loadUserPurchases();
  }, [isAuthenticated, user]); // Dependencias controladas con refs

  // Cargar estadísticas del tutor (solo para tutores)
  useEffect(() => {
    const loadTutorStats = async () => {
      if (!isAuthenticated() || !user) return;

      // Solo cargar para tutores
      const isTutor = user?.role === 'parent';
      if (!isTutor) return;

      try {
        const stats = await futbolistaService.getTutorStats();
        setTutorStats(stats);

        info('Estadísticas completas del tutor cargadas', stats);

      } catch (error) {
        logError('Error al cargar estadísticas del tutor', { error: error.message, stack: error.stack });
        setTutorStats({
          futbolistasRegistrados: 0,
          futbolistasTemporadaActual: 0,
          inscripcionesActivas: 0,
          torneosActivos: 0,
          temporadaActual: 'N/A'
        });
      }
    };

    loadTutorStats();
  }, [isAuthenticated, user]);

  // Cargar información de sede más cercana (para usuarios con localidad)
  useEffect(() => {
    const loadSedeInfo = async () => {
      if (!isAuthenticated() || !user || !profileData?.address?.city) return;

      setSedeInfo(prev => ({ ...prev, cargando: true }));

      try {
        const info = await sedeService.getInfoCompletaSedesCercanas(profileData.address.city);
        setSedeInfo({
          ...info,
          cargando: false
        });
      } catch (error) {
        console.error('Error al cargar información de sede:', error);
        setSedeInfo({
          tiene_sede_cercana: false,
          sede_cercana: null,
          cargando: false,
          mensaje: 'Error al buscar sedes cercanas'
        });
      }
    };

    loadSedeInfo();
  }, [isAuthenticated, user, profileData?.address?.city]);

  // Manejar la navegación a la página de edición
  const handleOpenEditModal = () => {
    navigate('/edit-profile');
  };

  // Abrir página de edición de perfil extendido
  const handleOpenExtendedProfileModal = () => {
    navigate('/edit-profile');
  };

  if (loading || !profileData) {
    return (
      <div className="profile-section">
        <div className="container">
          <div className="profile-header">
            <h1>Cargando perfil...</h1>
            <p>Por favor espere mientras cargamos su información.</p>
          </div>
        </div>
      </div>
    );
  }

  // Extraer datos del perfil para facilitar el acceso
  const {
    name,
    email,
    phone,
    avatar,
    address,
    emergencyContact,
    extendedProfile,
    xpPoints = 0,
    wallet = 0,
    walletBalance = 0,
  } = profileData;

  // Normalizar datos para compatibilidad
  const normalizedWalletBalance = walletBalance || wallet || 0;

  // Verificar si el usuario es un tutor (no debe ver información de futbolistas)
  const isTutor = user?.role === 'parent';

  return (
    <div className="profile-section">
      {/* Banner para completar perfil - Solo para usuarios no tutores */}
      {!isTutor && (
        <ProfileCompletionBanner
          onCompleteProfile={handleOpenExtendedProfileModal}
        />
      )}

      <div className="container" style={{
        padding: isMobile ? '0 16px' : '0 20px',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>
        <div className="profile-header" style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'center' : 'flex-start',
          gap: isMobile ? '20px' : '30px',
          padding: isMobile ? '20px 0' : '30px 0',
          textAlign: isMobile ? 'center' : 'left',
        }}>
          <div className="profile-avatar" style={{
            position: 'relative',
            width: isMobile ? '120px' : '150px',
            height: isMobile ? '120px' : '150px',
            borderRadius: '50%',
            overflow: 'hidden',
            border: '4px solid #4352A9',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            flexShrink: 0,
          }}>
            {avatar && (avatar.startsWith('data:image/') || avatar.startsWith('http') || avatar.startsWith('/')) ? (
              <img
                src={avatar}
                alt={name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <DefaultAvatar />
            )}
            <button
              className="change-avatar-btn"
              onClick={handleOpenEditModal}
              title="Cambiar avatar"
              style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                width: isMobile ? '36px' : '40px',
                height: isMobile ? '36px' : '40px',
                borderRadius: '50%',
                backgroundColor: '#4352A9',
                color: 'white',
                border: '2px solid white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '18px' : '20px',
                transition: 'all 0.3s ease',
              }}
            >
              <span className="material-icons">photo_camera</span>
            </button>
          </div>

          <div className="profile-info" style={{
            flex: 1,
            width: isMobile ? '100%' : 'auto',
          }}>
            <div className="profile-title-section" style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'center' : 'flex-start',
              justifyContent: isMobile ? 'center' : 'space-between',
              gap: isMobile ? '16px' : '20px',
              marginBottom: '16px',
            }}>
              <h1 style={{
                fontSize: isMobile ? '1.8rem' : '2.2rem',
                fontWeight: '700',
                color: '#061156',
                margin: '0',
                lineHeight: '1.2',
              }}>
                👋 {name}
              </h1>
              <button
                className="btn btn-primary edit-profile-btn"
                onClick={handleOpenEditModal}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: isMobile ? '12px 20px' : '10px 16px',
                  fontSize: isMobile ? '16px' : '14px',
                  minHeight: isMobile ? '48px' : '40px',
                  borderRadius: '8px',
                  backgroundColor: '#4352A9',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                <span className="material-icons" style={{ fontSize: '18px' }}>edit</span>
                {isMobile ? 'Editar' : 'Editar Perfil'}
              </button>
            </div>
            <div className="profile-meta" style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: isMobile ? '12px' : '20px',
              marginBottom: '24px',
            }}>
              <div className="profile-meta-item" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: isMobile ? '16px' : '14px',
                color: '#495057',
                padding: isMobile ? '8px 0' : '4px 0',
              }}>
                <span className="material-icons" style={{
                  fontSize: isMobile ? '20px' : '18px',
                  color: '#4352A9'
                }}>email</span>
                <span style={{ wordBreak: 'break-word' }}>{email}</span>
              </div>
              {phone && (
                <div className="profile-meta-item" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: isMobile ? '16px' : '14px',
                  color: '#495057',
                  padding: isMobile ? '8px 0' : '4px 0',
                }}>
                  <span className="material-icons" style={{
                    fontSize: isMobile ? '20px' : '18px',
                    color: '#4352A9'
                  }}>phone</span>
                  <span>{phone}</span>
                </div>
              )}
              {/* Mostrar localidad solo para tutores */}
              {isTutor && address?.city && (
                <div className="profile-meta-item" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: isMobile ? '16px' : '14px',
                  color: '#495057',
                  padding: isMobile ? '8px 0' : '4px 0',
                }}>
                  <span className="material-icons" style={{
                    fontSize: isMobile ? '20px' : '18px',
                    color: '#4352A9'
                  }}>location_on</span>
                  <span>{address.city}</span>
                </div>
              )}
            </div>

            <div className="profile-stats" style={{
              display: 'grid',
              gridTemplateColumns: isMobile ?
                (isSmallMobile ? '1fr' : 'repeat(2, 1fr)') :
                'repeat(auto-fit, minmax(200px, 1fr))',
              gap: isMobile ? '16px' : '20px',
              marginBottom: '30px',
            }}>
              {/* Estadísticas para tutores */}
              {isTutor ? (
                <>
                  <div className="profile-stat" style={{
                    backgroundColor: '#ffffff',
                    padding: isMobile ? '20px' : '24px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                    border: '1px solid #e1e5e9',
                    transition: 'transform 0.3s ease',
                  }}>
                    <div className="stat-value" style={{
                      fontSize: isMobile ? '1.8rem' : '2rem',
                      fontWeight: '700',
                      color: '#4352A9',
                      marginBottom: '8px',
                      lineHeight: '1.2',
                    }}>👥 {tutorStats.futbolistasRegistrados}</div>
                    <div className="stat-label" style={{
                      fontSize: isMobile ? '14px' : '13px',
                      color: '#6c757d',
                      fontWeight: '500',
                    }}>Futbolistas Registrados</div>
                  </div>
                  <div className="profile-stat" style={{
                    backgroundColor: '#ffffff',
                    padding: isMobile ? '20px' : '24px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                    border: '1px solid #e1e5e9',
                    transition: 'transform 0.3s ease',
                  }}>
                    <div className="stat-value" style={{
                      fontSize: isMobile ? '1.8rem' : '2rem',
                      fontWeight: '700',
                      color: '#28a745',
                      marginBottom: '8px',
                      lineHeight: '1.2',
                    }}>⚽ {tutorStats.futbolistasTemporadaActual}</div>
                    <div className="stat-label" style={{
                      fontSize: isMobile ? '14px' : '13px',
                      color: '#6c757d',
                      fontWeight: '500',
                    }}>Activos {tutorStats.temporadaActual}</div>
                  </div>
                  <div className="profile-stat" style={{
                    backgroundColor: '#ffffff',
                    padding: isMobile ? '20px' : '24px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                    border: '1px solid #e1e5e9',
                    transition: 'transform 0.3s ease',
                  }}>
                    <div className="stat-value" style={{
                      fontSize: isMobile ? '1.8rem' : '2rem',
                      fontWeight: '700',
                      color: '#ffc107',
                      marginBottom: '8px',
                      lineHeight: '1.2',
                    }}>🏆 {tutorStats.inscripcionesActivas}</div>
                    <div className="stat-label" style={{
                      fontSize: isMobile ? '14px' : '13px',
                      color: '#6c757d',
                      fontWeight: '500',
                    }}>Inscripciones Activas</div>
                  </div>
                  <div className="profile-stat" style={{
                    backgroundColor: '#ffffff',
                    padding: isMobile ? '20px' : '24px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                    border: '1px solid #e1e5e9',
                    transition: 'transform 0.3s ease',
                  }}>
                    <div className="stat-value" style={{
                      fontSize: isMobile ? '1.8rem' : '2rem',
                      fontWeight: '700',
                      color: '#dc3545',
                      marginBottom: '8px',
                      lineHeight: '1.2',
                    }}>🏟️ {tutorStats.torneosActivos}</div>
                    <div className="stat-label" style={{
                      fontSize: isMobile ? '14px' : '13px',
                      color: '#6c757d',
                      fontWeight: '500',
                    }}>Torneos Activos</div>
                  </div>
                </>
              ) : (
                /* Estadísticas para futbolistas */
                <>
                  <div className="profile-stat" style={{
                    backgroundColor: '#ffffff',
                    padding: isMobile ? '20px' : '24px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                    border: '1px solid #e1e5e9',
                    transition: 'transform 0.3s ease',
                  }}>
                    <div className="stat-value" style={{
                      fontSize: isMobile ? '1.8rem' : '2rem',
                      fontWeight: '700',
                      color: '#4352A9',
                      marginBottom: '8px',
                      lineHeight: '1.2',
                    }}>⭐ {xpPoints}</div>
                    <div className="stat-label" style={{
                      fontSize: isMobile ? '14px' : '13px',
                      color: '#6c757d',
                      fontWeight: '500',
                    }}>Puntos XP</div>
                  </div>
                  <div className="profile-stat" style={{
                    backgroundColor: '#ffffff',
                    padding: isMobile ? '20px' : '24px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                    border: '1px solid #e1e5e9',
                    transition: 'transform 0.3s ease',
                  }}>
                    <div className="stat-value" style={{
                      fontSize: isMobile ? '1.8rem' : '2rem',
                      fontWeight: '700',
                      color: '#28a745',
                      marginBottom: '8px',
                      lineHeight: '1.2',
                    }}>💰 €{normalizedWalletBalance}</div>
                    <div className="stat-label" style={{
                      fontSize: isMobile ? '14px' : '13px',
                      color: '#6c757d',
                      fontWeight: '500',
                    }}>Monedero</div>
                  </div>
                </>
              )}
            </div>

            {/* Badge de sede más cercana - Solo si hay localidad configurada */}
            {address?.city && (
              <div className="profile-stats sede-cercana-info" style={{
                backgroundColor: '#f8f9fa',
                padding: isMobile ? '20px' : '24px',
                borderRadius: '12px',
                border: '1px solid #e1e5e9',
                marginBottom: '24px',
              }}>
                <h4 className="sede-section-title" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: isMobile ? '1.2rem' : '1.3rem',
                  fontWeight: '600',
                  color: '#061156',
                  marginBottom: '16px',
                  textAlign: isMobile ? 'center' : 'left',
                }}>
                  <span className="material-icons" style={{
                    fontSize: isMobile ? '24px' : '26px',
                    color: '#4352A9'
                  }}>location_on</span>
                  Sede más cercana desde {address.city}
                </h4>
                {sedeInfo.cargando ? (
                  <div className="profile-stat sede-stat loading" style={{
                    backgroundColor: '#ffffff',
                    padding: isMobile ? '20px' : '24px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: '1px solid #e1e5e9',
                  }}>
                    <div className="stat-value" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      fontSize: isMobile ? '1rem' : '1.1rem',
                      fontWeight: '600',
                      color: '#6c757d',
                      marginBottom: '8px',
                    }}>
                      <span className="material-icons" style={{ fontSize: '20px' }}>hourglass_empty</span>
                      Buscando...
                    </div>
                    <div className="stat-label" style={{
                      fontSize: isMobile ? '14px' : '13px',
                      color: '#6c757d',
                    }}>Calculando distancias a sedes disponibles</div>
                  </div>
                ) : sedeInfo.tiene_sede_cercana ? (
                  <div className="profile-stat sede-stat" style={{
                    backgroundColor: '#ffffff',
                    padding: isMobile ? '20px' : '24px',
                    borderRadius: '8px',
                    border: '1px solid #e1e5e9',
                    textAlign: isMobile ? 'center' : 'left',
                  }}>
                    <div className="stat-value" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isMobile ? 'center' : 'flex-start',
                      gap: '8px',
                      fontSize: isMobile ? '1.2rem' : '1.3rem',
                      fontWeight: '700',
                      color: '#4352A9',
                      marginBottom: '12px',
                    }}>
                      <span className="material-icons" style={{ fontSize: '24px' }}>stadium</span>
                      {sedeInfo.sede_cercana.nombre}
                    </div>
                    <div className="stat-label" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isMobile ? 'center' : 'flex-start',
                      gap: '8px',
                      fontSize: isMobile ? '14px' : '15px',
                      color: '#495057',
                      marginBottom: '12px',
                    }}>
                      <span className="material-icons" style={{ fontSize: '18px' }}>place</span>
                      {sedeInfo.sede_cercana.poblacion} • {sedeInfo.sede_cercana.distancia}
                    </div>
                    <div className="stat-extra" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isMobile ? 'center' : 'flex-start',
                      gap: '8px',
                      fontSize: isMobile ? '13px' : '14px',
                      color: '#6c757d',
                      marginBottom: '8px',
                      flexWrap: 'wrap',
                    }}>
                      <span className="material-icons" style={{ fontSize: '16px' }}>sports_soccer</span>
                      <span>{sedeInfo.sede_cercana.campos_f8} campos F8 • {sedeInfo.sede_cercana.campos_f11} campos F11</span>
                      {sedeInfo.total_sedes_cercanas > 1 && (
                        <span className="sedes-adicionales" style={{
                          color: '#4352A9',
                          fontWeight: '500',
                        }}>
                          • +{sedeInfo.total_sedes_cercanas - 1} más en la zona
                        </span>
                      )}
                    </div>
                    {sedeInfo.sede_cercana.contacto && (
                      <div className="stat-contact" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isMobile ? 'center' : 'flex-start',
                        gap: '8px',
                        fontSize: isMobile ? '14px' : '15px',
                        color: '#28a745',
                        fontWeight: '500',
                      }}>
                        <span className="material-icons" style={{ fontSize: '18px' }}>phone</span>
                        {sedeInfo.sede_cercana.contacto}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="profile-stat sede-stat no-sede">
                    <div className="stat-value">
                      <span className="material-icons">location_off</span>
                      Sin sedes cercanas
                    </div>
                    <div className="stat-label">No hay sedes disponibles en un radio de 100km</div>
                    <div className="stat-extra">
                      Contacta con el administrador para más información
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Badge de torneos activos - Solo si hay sede cercana */}
            {address?.city && sedeInfo.tiene_sede_cercana && sedeInfo.torneos_info && (
              <div className="profile-stats torneos-activos-info">
                <h4 className="sede-section-title">
                  <span className="material-icons">emoji_events</span>
                  Torneos activos en {sedeInfo.sede_cercana.poblacion}
                </h4>
                {sedeInfo.torneos_info.tiene_torneos_activos ? (
                  <>
                    <div className="profile-stat torneo-stat">
                      <div className="stat-value">
                        <span className="material-icons">sports</span>
                        {sedeInfo.torneos_info.torneos_activos.count} torneo{sedeInfo.torneos_info.torneos_activos.count !== 1 ? 's' : ''} activo{sedeInfo.torneos_info.torneos_activos.count !== 1 ? 's' : ''}
                      </div>
                      <div className="stat-label">
                        {sedeInfo.torneos_info.torneos_activos.data[0]?.nombre || 'Torneo disponible'}
                      </div>
                      {sedeInfo.torneos_info.torneos_activos.data[0]?.temporada_nombre && (
                        <div className="stat-extra">
                          <span className="material-icons">calendar_today</span>
                          Temporada {sedeInfo.torneos_info.torneos_activos.data[0].temporada_nombre}
                        </div>
                      )}
                    </div>

                    {/* Próxima jornada */}
                    {sedeInfo.torneos_info.proxima_jornada && (
                      <div className="profile-stat jornada-stat">
                        <div className="stat-value">
                          <span className="material-icons">event</span>
                          Próxima jornada #{sedeInfo.torneos_info.proxima_jornada.numero}
                        </div>
                        <div className="stat-label">
                          <span className="material-icons">schedule</span>
                          {formatFechaJornada(sedeInfo.torneos_info.proxima_jornada.fecha)}
                        </div>
                        {sedeInfo.torneos_info.proxima_jornada.fecha_limite_inscripcion && (
                          <div className="stat-extra">
                            <span className="material-icons">access_time</span>
                            Inscripciones hasta {new Date(sedeInfo.torneos_info.proxima_jornada.fecha_limite_inscripcion).toLocaleDateString('es-ES')}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="profile-stat torneo-stat no-torneos">
                    <div className="stat-value">
                      <span className="material-icons">sports_off</span>
                      Sin torneos activos
                    </div>
                    <div className="stat-label">No hay torneos programados en esta sede</div>
                    <div className="stat-extra">
                      Mantente atento a futuras convocatorias
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Sección Mi Sede - Solo para usuarios no tutores */}
        {!isTutor && (
          <div className="profile-details">
            <div className="profile-section-title">
              <h2>🏟️ Mi Sede</h2>
            </div>

            <div className="venue-card">
              <div className="venue-card-content">
                {profileData?.extendedProfile?.venue ? (
                  <div className="venue-info">
                    <div className="venue-image">
                      <img
                        src={profileData.extendedProfile.venue.mainImage || '/img/default-avatar.svg'}
                        alt={profileData.extendedProfile.venue.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/img/default-avatar.svg';
                        }}
                      />
                    </div>
                    <div className="venue-details">
                      <h4>{profileData.extendedProfile.venue.name}</h4>
                      <p>
                        <span className="material-icons">place</span>
                        {profileData.extendedProfile.venue.address?.city}, {profileData.extendedProfile.venue.address?.street}
                      </p>
                      <p>
                        <span className="material-icons">star</span>
                        {profileData.extendedProfile.venue.rating || '4.5'} ({profileData.extendedProfile.venue.reviewsCount || '0'} reseñas)
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="no-venue">
                    <span className="material-icons">location_off</span>
                    <p>No tienes una sede asignada</p>
                    <div className="venue-card-actions">
                      <button className="btn btn-sm btn-outline" onClick={handleOpenEditModal}>
                        Seleccionar Sede
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sección Mis Jornadas - Solo para usuarios no tutores */}
        {!isTutor && (
          <div className="profile-details">
          <div className="profile-section-title">
            <h2>🎫 Mis Jornadas</h2>
          </div>

          <div className="purchases-section">
            {purchasesLoading ? (
              <div className="loading-purchases">
                <div className="spinner"></div>
                <p>Cargando jornadas...</p>
              </div>
            ) : purchases.length > 0 ? (
              <>
                {/* Resumen de sesiones */}
                {sessionsSummary && (
                  <div className="sessions-summary-card">
                    <div className="summary-stats">
                      <div className="summary-stat">
                        <div className="stat-value">{sessionsSummary.totalSessions}</div>
                        <div className="stat-label">Total Jornadas</div>
                      </div>
                      <div className="summary-stat">
                        <div className="stat-value">{sessionsSummary.usedSessions}</div>
                        <div className="stat-label">Utilizadas</div>
                      </div>
                      <div className="summary-stat">
                        <div className="stat-value">{sessionsSummary.remainingSessions}</div>
                        <div className="stat-label">Disponibles</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lista de compras */}
                <div className="purchases-grid">
                  {purchases.map((purchase) => (
                    <div key={purchase._id} className="purchase-card">
                      <div className="purchase-header">
                        <h4>{purchase.product?.name || purchase.productSnapshot?.name}</h4>
                        <span className={`purchase-status status-${purchaseService.formatPurchaseStatus(purchase.status).color}`}>
                          {purchaseService.formatPurchaseStatus(purchase.status).text}
                        </span>
                      </div>

                      <div className="purchase-details">
                        <div className="purchase-sessions">
                          <div className="sessions-progress">
                            <div className="sessions-text">
                              {purchase.usedSessions}/{purchase.totalSessions} jornadas utilizadas
                            </div>
                            <div className="progress-bar-container">
                              <div
                                className="progress-bar"
                                style={{ width: `${purchaseService.calculateUsageProgress(purchase)}%` }}
                              />
                            </div>
                            <div className="sessions-remaining">
                              {purchase.remainingSessions} jornadas disponibles
                            </div>
                          </div>
                        </div>

                        <div className="purchase-info">
                          <div className="purchase-date">
                            <span className="material-icons">event</span>
                            Comprado: {purchaseService.formatPurchaseDate(purchase.purchaseDate)}
                          </div>

                          {purchase.expirationDate && (
                            <div className="purchase-expiration">
                              <span className="material-icons">schedule</span>
                              Expira: {purchaseService.formatPurchaseDate(purchase.expirationDate)}
                              {purchaseService.isNearExpiration(purchase) && (
                                <span className="expiration-warning">¡Próximo a expirar!</span>
                              )}
                            </div>
                          )}

                          <div className="purchase-payment">
                            <span className="material-icons">payment</span>
                            {purchaseService.formatPaymentMethod(purchase.paymentMethod)} - {purchaseService.formatPrice(purchase.amountPaid)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="no-purchases">
                <span className="material-icons">shopping_cart</span>
                <h3>No tienes jornadas adquiridas</h3>
                <p>Compra packs de jornadas para poder participar en los partidos</p>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/products')}
                >
                  Ver Productos
                </button>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Sección Mi Información Personal - Solo para usuarios no tutores */}
        {!isTutor && (
          <div className="profile-details">
            <div className="profile-section-title">
              <h2>📋 Mi Información Personal</h2>
            </div>

          <div className="info-grid">

            {address && (address.street || address.city || address.postalCode || address.country) && (
              <div className="info-card" data-card-type="address">
                <h3>🏠 Dirección</h3>
                {address.street && (
                  <div className="info-item">
                    <span className="info-label">Dirección</span>
                    <span className="info-value">{address.street}</span>
                  </div>
                )}
                {address.city && (
                  <div className="info-item">
                    <span className="info-label">Ciudad</span>
                    <span className="info-value">{address.city}</span>
                  </div>
                )}
                {address.postalCode && (
                  <div className="info-item">
                    <span className="info-label">Código postal</span>
                    <span className="info-value">{address.postalCode}</span>
                  </div>
                )}
                {address.country && (
                  <div className="info-item">
                    <span className="info-label">País</span>
                    <span className="info-value">{address.country}</span>
                  </div>
                )}
              </div>
            )}

            {(profileData.parentName || extendedProfile?.parentName ||
             (emergencyContact && (emergencyContact.name || emergencyContact.phone))) ? (
              <div className="info-card" data-card-type="parent">
                <h3>👨‍👩‍👧‍👦 Información del Padre/Madre/Tutor</h3>
                {(profileData.parentName || extendedProfile?.parentName) && (
                  <div className="info-item">
                    <span className="info-label">👤 Nombre</span>
                    <span className="info-value">{profileData.parentName || extendedProfile?.parentName}</span>
                  </div>
                )}
                {profileData.parentPhone && (
                  <div className="info-item">
                    <span className="info-label">📱 Teléfono</span>
                    <span className="info-value">{profileData.parentPhone}</span>
                  </div>
                )}
                {emergencyContact?.name && (
                  <div className="info-item">
                    <span className="info-label">🚨 Contacto emergencia</span>
                    <span className="info-value">{emergencyContact.name}</span>
                  </div>
                )}
                {emergencyContact?.phone && (
                  <div className="info-item">
                    <span className="info-label">📞 Teléfono emergencia</span>
                    <span className="info-value">{emergencyContact.phone}</span>
                  </div>
                )}
              </div>
            ) : null}

            {extendedProfile && (
              <>

                {(profileData.childName || profileData.childBirthDate || profileData.team ||
                  extendedProfile?.childName || extendedProfile?.childBirthDate || extendedProfile?.team) && (
                  <div className="info-card" data-card-type="child">
                    <h3>🧒 Información del Niño</h3>
                    {(profileData.childName || extendedProfile?.childName) && (
                      <div className="info-item">
                        <span className="info-label">👤 Nombre</span>
                        <span className="info-value">{profileData.childName || extendedProfile?.childName}</span>
                      </div>
                    )}
                    {(profileData.childBirthDate || extendedProfile?.childBirthDate) && (
                      <div className="info-item">
                        <span className="info-label">🎂 Fecha de nacimiento</span>
                        <span className="info-value">
                          {new Date(profileData.childBirthDate || extendedProfile?.childBirthDate).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    )}
                    {(profileData.team || extendedProfile?.team) && (
                      <div className="info-item">
                        <span className="info-label">⚽ Equipo</span>
                        <span className="info-value">{profileData.team || extendedProfile?.team}</span>
                      </div>
                    )}
                  </div>
                )}

                {(profileData.region || profileData.division || profileData.playingPosition || profileData.secondaryPosition ||
                  extendedProfile?.venue || extendedProfile?.region || extendedProfile?.division || extendedProfile?.playingPosition || extendedProfile?.secondaryPosition) && (
                  <div className="info-card" data-card-type="sports">
                    <h3>⚽ Información Deportiva</h3>
                    {extendedProfile?.venue && (
                      <div className="info-item">
                        <span className="info-label">🏟️ Sede</span>
                        <span className="info-value">{extendedProfile.venue.name || 'No especificado'}</span>
                      </div>
                    )}
                    {(profileData.region || extendedProfile?.region) && (
                      <div className="info-item">
                        <span className="info-label">🗺️ Región</span>
                        <span className="info-value">
                          {(profileData.region || extendedProfile?.region) === 'valencia' ? 'Comunidad Valenciana' :
                           (profileData.region || extendedProfile?.region) === 'murcia' ? 'Región de Murcia' :
                           (profileData.region || extendedProfile?.region)}
                        </span>
                      </div>
                    )}
                    {(profileData.division || extendedProfile?.division) && (
                      <div className="info-item">
                        <span className="info-label">🏆 División</span>
                        <span className="info-value">{profileData.division || extendedProfile?.division}</span>
                      </div>
                    )}
                    {(profileData.playingPosition || extendedProfile?.playingPosition) && (
                      <div className="info-item">
                        <span className="info-label">🎯 Posición principal</span>
                        <span className="info-value">{profileData.playingPosition || extendedProfile?.playingPosition}</span>
                      </div>
                    )}
                    {(profileData.secondaryPosition || extendedProfile?.secondaryPosition) && (
                      <div className="info-item">
                        <span className="info-label">🔄 Posición secundaria</span>
                        <span className="info-value">{profileData.secondaryPosition || extendedProfile?.secondaryPosition}</span>
                      </div>
                    )}
                  </div>
                )}

                {(extendedProfile.familyInstagram || extendedProfile.hasEquipment !== null || extendedProfile.howDidYouKnow) && (
                  <div className="info-card" data-card-type="additional">
                    <h3>ℹ️ Adicional</h3>
                    {extendedProfile.familyInstagram && (
                      <div className="info-item">
                        <span className="info-label">Instagram</span>
                        <span className="info-value">{extendedProfile.familyInstagram}</span>
                      </div>
                    )}
                    {extendedProfile.hasEquipment !== null && (
                      <div className="info-item">
                        <span className="info-label">Equipación</span>
                        <span className="info-value">
                          {extendedProfile.hasEquipment ? 'Sí' : 'No'}
                        </span>
                      </div>
                    )}
                    {extendedProfile.hasEquipment === false && extendedProfile.equipmentSize && (
                      <div className="info-item">
                        <span className="info-label">Talla</span>
                        <span className="info-value">{extendedProfile.equipmentSize}</span>
                      </div>
                    )}
                    {extendedProfile.hasEquipment === false && extendedProfile.jerseyName && (
                      <div className="info-item">
                        <span className="info-label">Nombre camiseta</span>
                        <span className="info-value">{extendedProfile.jerseyName}</span>
                      </div>
                    )}
                    {extendedProfile.howDidYouKnow && (
                      <div className="info-item">
                        <span className="info-label">Nos conoció</span>
                        <span className="info-value">
                          {extendedProfile.howDidYouKnow === 'instagram' ? 'Instagram' :
                           extendedProfile.howDidYouKnow === 'facebook' ? 'Facebook' :
                           extendedProfile.howDidYouKnow === 'web' ? 'Web' :
                           extendedProfile.howDidYouKnow === 'cliente_anterior' ? 'Cliente anterior' :
                           extendedProfile.howDidYouKnow === 'recomendacion' ? 'Recomendación' :
                           extendedProfile.howDidYouKnow === 'otro' ? 'Otro' :
                           extendedProfile.howDidYouKnow}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        )}
      </div>

    </div>
  );
};

export default DirectProfile;
